import { action, computed, makeObservable } from 'mobx';

import { ValueModel } from '@/models/ValueModel';
import {
  QueueRef,
  Sprint,
  TaskFilters,
  TaskPriorityRef,
  TaskStatusRef,
  TaskTypeRef,
  UserRef,
} from '@/tracker/types';

/** Выбранные значения фильтров: целиком, а не id — плашкам нужны названия */
export type TaskFilterValues = {
  statuses: TaskStatusRef[];
  types: TaskTypeRef[];
  priorities: TaskPriorityRef[];
  assignees: UserRef[];
  authors: UserRef[];
  sprints: Sprint[];
  queues: QueueRef[];
};

export type TaskFilterField = keyof TaskFilterValues;

const EMPTY_VALUES: TaskFilterValues = {
  statuses: [],
  types: [],
  priorities: [],
  assignees: [],
  authors: [],
  sprints: [],
  queues: [],
};

const keysOf = (items: { key: string }[]) => items.map((item) => item.key);
const idsOf = (items: { id: string }[]) => items.map((item) => item.id);

/** Фильтры задач как в Яндекс Трекере: внутри поля значения через «ИЛИ», поля — через «И» */
export class TaskFiltersModel {
  private readonly _values = new ValueModel<TaskFilterValues>(EMPTY_VALUES);

  constructor() {
    makeObservable(this, {
      values: computed,
      hasValues: computed,
      set: action.bound,
      reset: action.bound,
    });
  }

  get values() {
    return this._values.value;
  }

  get hasValues() {
    return Object.values(this._values.value).some((values) => values.length > 0);
  }

  set<K extends TaskFilterField>({ field, value }: { field: K; value: TaskFilterValues[K] }) {
    this._values.change({ ...this._values.value, [field]: value });
  }

  reset() {
    this._values.change(EMPTY_VALUES);
  }

  /** Условия поиска задач по выбранному; экран дополняет их своими (очередь, доска, спринт) */
  toTaskFilters(): TaskFilters {
    const { statuses, types, priorities, assignees, authors, sprints, queues } = this._values.value;
    const filters: TaskFilters = {};

    if (statuses.length) filters.status = { in: keysOf(statuses) };

    if (types.length) filters.type = { in: keysOf(types) };

    if (priorities.length) filters.priority = { in: keysOf(priorities) };

    if (assignees.length) filters.assignee = { in: idsOf(assignees) };

    if (authors.length) filters.createdBy = { in: idsOf(authors) };

    if (sprints.length) filters.sprint = { in: idsOf(sprints) };

    if (queues.length) filters.queue = { in: keysOf(queues) };

    return filters;
  }
}
